import type { AxiosInstance, AxiosResponse } from 'axios'
import axios from 'axios'
import Cookies, { CookieAttributes } from 'js-cookie'

import type { TokensManager } from './TokensManager'

const COOKIES_SET_OPTIONS: CookieAttributes =
  process.env.VUE_APP_ENVIRONMENT === 'development'
    ? {
        domain: '.localhost',
        path: '/',
        secure: false,
        sameSite: 'Lax',
        expires: 30,
      }
    : {
        domain: '.thegogame.com',
        path: '/',
        secure: true,
        sameSite: 'None',
        expires: 30,
      }

const COOKIES_REMOVE_OPTIONS: CookieAttributes =
  process.env.VUE_APP_ENVIRONMENT === 'development'
    ? {
        domain: '.localhost',
        path: '/',
      }
    : {
        domain: '.thegogame.com',
        path: '/',
      }

const Key = {
  TOKEN: `tgg_${process.env.VUE_APP_MODE}_token`,
  REFRESH_TOKEN: `tgg_${process.env.VUE_APP_MODE}_refresh_token`,
}

export default class TokensManagerImpl implements TokensManager {
  private _refreshRequest: Promise<RefreshResponse> | null = null

  constructor(private readonly client: AxiosInstance) {}

  get token(): string | undefined {
    return Cookies.get(Key.TOKEN) ?? undefined
  }

  set token(value: string | undefined) {
    if (value !== undefined) {
      Cookies.set(Key.TOKEN, value, COOKIES_SET_OPTIONS)
    } else {
      Cookies.remove(Key.TOKEN, COOKIES_REMOVE_OPTIONS)
    }
  }

  get refreshToken(): string | undefined {
    return Cookies.get(Key.REFRESH_TOKEN) ?? undefined
  }

  set refreshToken(value: string | undefined) {
    if (value !== undefined) {
      Cookies.set(Key.REFRESH_TOKEN, value, COOKIES_SET_OPTIONS)
    } else {
      Cookies.remove(Key.REFRESH_TOKEN, COOKIES_REMOVE_OPTIONS)
    }
  }

  async refresh(): Promise<string | undefined> {
    if (this.refreshToken === undefined) return undefined
    if (this._refreshRequest === null) {
      this._refreshRequest = this.client.post('/auth/refresh', {
        refreshToken: this.refreshToken,
      })
    }
    try {
      const {
        data: { token },
      } = await this._refreshRequest
      this.token = token
      return token
    } catch (e) {
      if (axios.isAxiosError(e)) return undefined
      throw e
    } finally {
      this._refreshRequest = null
    }
  }

  clear(): void {
    this.token = undefined
    this.refreshToken = undefined
  }
}

export type RefreshResponse = AxiosResponse<{
  token: string
}>
